import type { JsonArray, JsonObject, JsonValue } from "./types";

export function isJsonObject(
  value: JsonValue | null | undefined,
): value is JsonObject {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === "object" &&
    !Array.isArray(value)
  );
}

export function cloneJsonValue<T extends JsonValue>(value: T): T {
  return structuredClone(value);
}

export function cloneJsonObject(value: JsonObject): JsonObject {
  return structuredClone(value);
}

export function coerceToJsonObject(value: unknown): JsonObject {
  const jsonValue = coerceToJsonValue(value);
  if (jsonValue && typeof jsonValue === "object" && !Array.isArray(jsonValue)) {
    return jsonValue;
  }
  return {};
}

/**
 * Attempts to coerce arbitrary data into a JSON-compatible value.
 *
 * - `undefined` entries are dropped (object properties) or preserved as `null` when encountered
 *   directly or inside arrays.
 * - Primitive strings, numbers, and booleans pass through unchanged.
 * - Functions, symbols, and other unsupported types yield `null`.
 */
export function coerceToJsonValue(value: unknown): JsonValue | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.reduce<JsonArray>((result, entry) => {
      const coerced = coerceToJsonValue(entry);
      result.push(coerced);
      return result;
    }, []);
  }
  if (typeof value === "object") {
    const result: JsonObject = {};
    for (const [key, entry] of Object.entries(value)) {
      if (entry === undefined) {
        continue;
      }
      result[key] = coerceToJsonValue(entry);
    }
    return result;
  }
  return null;
}
